/**
 * MODULE: domain/templates/frontend/lib/emailTemplateGoldenPath.ts
 * PURPOSE: Golden data path for tenant email templates (Assistant, Sales Hub, Messaging).
 * DEPENDENCIES: ../api/templatesApi, ../../contracts/templates.types
 * PUBLIC_API: listEmailTemplates, getEmailTemplate, extractEmailTemplateSource, GOLDEN_EMAIL_TEMPLATE_FILTERS
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 95 / 150 lines
 */

import { templatesApi } from '../api/templatesApi';
import type { Template, TemplateFilters } from '../../contracts/templates.types';

/** Single list contract: Sales Hub Library, share modal, Messaging sequences, Assistant resolver HTTP. */
export const GOLDEN_EMAIL_TEMPLATE_API_PATH = '/api/v1/templates' as const;
export const GOLDEN_EMAIL_TEMPLATE_TYPE = 'email' as const;

export const GOLDEN_EMAIL_TEMPLATE_FILTERS: TemplateFilters = {
  type: GOLDEN_EMAIL_TEMPLATE_TYPE,
  page_size: 100,
  page: 1,
};

export type EmailTemplateSource = {
  subject: string;
  body: string;
};

/** Canonical subject/body extraction — keep in sync with _shared/lib/backend/email_template_golden_path.py */
export function extractEmailTemplateSource(t: Template): EmailTemplateSource {
  const c = t.content as Record<string, unknown> | undefined;
  const meta = t.metadata as Record<string, unknown> | undefined;
  const subject =
    (typeof c?.subject === 'string' && c.subject) ||
    (typeof meta?.email_subject === 'string' && meta.email_subject) ||
    (typeof meta?.share_email_subject === 'string' && meta.share_email_subject) ||
    t.title ||
    '';

  let body = '';
  if (typeof c?.body === 'string' && c.body) {
    body = c.body;
  } else if (t.generated_text?.trim()) {
    body = t.generated_text.trim();
  } else if (c && typeof c === 'object') {
    const keys = ['greeting', 'body', 'call_to_action', 'closing', 'ps_line'] as const;
    body = keys
      .map((k) => (typeof c[k] === 'string' ? (c[k] as string).trim() : ''))
      .filter(Boolean)
      .join('\n\n');
  }

  return { subject, body };
}

export async function listEmailTemplates(
  filters?: Partial<TemplateFilters>
): Promise<Template[]> {
  const res = await templatesApi.list({ ...GOLDEN_EMAIL_TEMPLATE_FILTERS, ...filters });
  return res.data ?? [];
}

export async function getEmailTemplate(id: string): Promise<{
  template: Template;
  subject: string;
  body: string;
}> {
  const template = await templatesApi.get(id);
  const { subject, body } = extractEmailTemplateSource(template);
  return { template, subject, body };
}

/** Map legacy `{FIRST_NAME}` placeholders to golden `{{first_name}}` merge tags. */
const LEGACY_MERGE_TO_GOLDEN: Record<string, string> = {
  '{FIRST_NAME}': '{{first_name}}',
  '{LAST_NAME}': '{{last_name}}',
  '{FULL_NAME}': '{{full_name}}',
  '{EMAIL}': '{{email}}',
  '{TITLE}': '{{title}}',
  '{COMPANY_NAME}': '{{company}}',
  '{COMPANY}': '{{company}}',
  '{SENDER_NAME}': '{{sender_name}}',
};

export function normalizeEmailMergeTags(text: string): string {
  if (!text) return '';
  let out = text;
  for (const [legacy, golden] of Object.entries(LEGACY_MERGE_TO_GOLDEN)) {
    out = out.split(legacy).join(golden);
  }
  return out.replace(/\{\{([a-zA-Z0-9_]+)\}\}/g, (_, key: string) => `{{${key.toLowerCase()}}}`);
}

export async function updateEmailTemplate(
  id: string,
  payload: {
    title?: string;
    description?: string;
    subject?: string;
    body?: string;
    category?: string;
    preview_text?: string;
    shared_with?: 'everyone' | 'team' | 'private';
  }
): Promise<Template> {
  const content: Record<string, string> = {};
  if (payload.subject !== undefined) {
    content.subject = normalizeEmailMergeTags(payload.subject);
  }
  if (payload.body !== undefined) {
    content.body = normalizeEmailMergeTags(payload.body);
  }
  if (payload.preview_text !== undefined) {
    content.preview_text = payload.preview_text.trim();
  }

  const metadata: Record<string, unknown> = {};
  if (payload.category) {
    metadata.hub_category = payload.category;
    metadata.category = payload.category;
  }
  if (payload.shared_with) {
    metadata.shared_with = payload.shared_with;
  }

  return templatesApi.patch(id, {
    ...(payload.title !== undefined ? { title: payload.title.trim() } : {}),
    ...(payload.description !== undefined ? { description: payload.description.trim() || undefined } : {}),
    ...(Object.keys(content).length ? { content } : {}),
    ...(Object.keys(metadata).length ? { metadata } : {}),
    ...(payload.body !== undefined ? { generated_text: normalizeEmailMergeTags(payload.body) } : {}),
  });
}

export async function createEmailTemplate(payload: {
  title: string;
  subject: string;
  body: string;
  description?: string;
  category?: string;
  preview_text?: string;
  shared_with?: 'everyone' | 'team' | 'private';
}): Promise<Template> {
  return templatesApi.createEmail({
    title: payload.title.trim(),
    subject: normalizeEmailMergeTags(payload.subject),
    body: normalizeEmailMergeTags(payload.body),
    description: payload.description?.trim() || undefined,
    category: payload.category || 'outreach',
    preview_text: payload.preview_text?.trim() || undefined,
    shared_with: payload.shared_with ?? 'everyone',
  });
}

/** Adapter for /templates page grid (PresentationTemplate card shape). */
export function goldenEmailTemplateToPresentationShape(t: Template): {
  id: string;
  name: string;
  description?: string;
  template_type: 'email';
  category: string;
  layout_schema: {
    email_content: { subject: string; body_html: string; preview_text?: string };
    metadata?: Record<string, unknown>;
  };
  is_system: boolean;
  is_published: boolean;
  created_at: string;
  updated_at: string;
} {
  const { subject, body } = extractEmailTemplateSource(t);
  const meta = (t.metadata ?? {}) as Record<string, unknown>;
  const category =
    (typeof meta.hub_category === 'string' && meta.hub_category) ||
    (typeof meta.category === 'string' && meta.category) ||
    'outreach';
  const preview =
    typeof (t.content as Record<string, unknown> | undefined)?.preview_text === 'string'
      ? ((t.content as Record<string, unknown>).preview_text as string)
      : undefined;

  return {
    id: t.id,
    name: t.title,
    description: t.description,
    template_type: 'email',
    category,
    layout_schema: {
      email_content: {
        subject,
        body_html: body,
        preview_text: preview,
      },
      metadata: meta,
    },
    is_system: false,
    is_published: true,
    created_at: t.created_at,
    updated_at: t.updated_at,
  };
}
